import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';

import { isHeaderSafeToken } from '../api/models';

const STORAGE_KEY = 'zaklepuj_request_tokens';

/** Manage tokens stay valid until `expiresAt + 30 days` (contract §5). */
export const REQUEST_TOKEN_GRACE_MS = 30 * 86_400_000;
/** Fallback validity when the request's `expiresAt` is unknown. */
const DEFAULT_TOKEN_TTL_MS = 60 * 86_400_000;
/** Keep at most this many (most recent) tokens. */
const MAX_TOKENS = 20;

interface StoredToken {
  token: string;
  /** Epoch ms after which the entry is dropped. */
  expiresAt: number;
}

type TokenMap = Record<string, StoredToken>;

/**
 * Keeps the manage tokens of requests („Szukam noclegu”) created in this
 * browser: `{ [requestId]: { token, expiresAt } }` in localStorage. Expired
 * entries are pruned on load and on every write, the map is capped at
 * `MAX_TOKENS`. All storage access is guarded — private mode / blocked storage
 * degrades to an in-memory map.
 *
 * Several tabs share the storage: every write is a read-modify-write of the
 * STORED map (a stale tab never drops tokens another tab saved meanwhile), and
 * the `storage` event keeps the in-memory map of the other tabs in sync.
 *
 * Only tokens that can be sent in the `X-Request-Token` header are kept
 * (`isHeaderSafeToken`): a `?t=` of a crafted / mangled link (`ż`, a line
 * break, 10 kB of text) would otherwise be persisted for weeks and make every
 * request of that page fail before it is sent. Such a value is ignored — it
 * neither gets stored nor replaces the token already kept for the request.
 */
@Injectable({ providedIn: 'root' })
export class RequestTokensService {
  /**
   * false once a write failed (quota / Safari private mode): from then on the
   * in-memory map is the source of truth of this tab — reading the stale
   * storage back would drop the tokens that could not be persisted.
   */
  private storageWritable = true;
  private readonly _tokens = signal<TokenMap>(this.readStorage() ?? {});

  /** `{ [requestId]: manageToken }` of the non-expired entries. */
  readonly tokens = computed((): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [id, entry] of Object.entries(this._tokens())) out[id] = entry.token;
    return out;
  });
  readonly ids = computed(() => Object.keys(this._tokens()));
  readonly count = computed(() => Object.keys(this._tokens()).length);

  constructor() {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    const onStorage = (event: StorageEvent): void => {
      // `key === null` → another tab cleared the whole storage.
      if (event.key !== null && event.key !== STORAGE_KEY) return;
      const latest = this.readStorage();
      if (latest) this._tokens.set(latest);
    };
    window.addEventListener('storage', onStorage);
    inject(DestroyRef).onDestroy(() => window.removeEventListener('storage', onStorage));
  }

  get(id: string): string | null {
    const entry = this._tokens()[id];
    // No signal writes here — `get()` is read inside computed signals.
    if (!entry || entry.expiresAt <= Date.now()) return null;
    return entry.token;
  }

  has(id: string): boolean {
    return this.get(id) !== null;
  }

  /**
   * Stores the token. `requestExpiresAt` (ISO) is the request's expiry — the
   * entry lives until `requestExpiresAt + 30 days` (the backend's validity);
   * calling it again with a newer `expiresAt` (extend / edit) refreshes the
   * validity. A token that cannot be a header value is ignored (see the class
   * doc) — the token stored so far stays.
   */
  set(id: string, token: string, requestExpiresAt?: string | null): void {
    if (!id || !isHeaderSafeToken(token)) return;
    const expiresAt = this.computeExpiry(requestExpiresAt);
    this.write((map) => {
      const current = map[id];
      if (current && current.token === token && current.expiresAt === expiresAt) return null;
      return { ...map, [id]: { token, expiresAt } };
    });
  }

  remove(id: string): void {
    this.write((map) => {
      if (!(id in map)) return null;
      const next = { ...map };
      delete next[id];
      return next;
    });
  }

  clear(): void {
    this._tokens.set({});
    this.saveToStorage({});
  }

  private computeExpiry(requestExpiresAt?: string | null): number {
    const base = requestExpiresAt ? new Date(requestExpiresAt).getTime() : NaN;
    return Number.isNaN(base)
      ? Date.now() + DEFAULT_TOKEN_TTL_MS
      : base + REQUEST_TOKEN_GRACE_MS;
  }

  /**
   * Read-modify-write: `mutate` gets the map as currently STORED (falls back
   * to the in-memory map when the storage is unavailable / unreadable) and
   * returns the new map, or `null` when nothing changes (no write, no event
   * in the other tabs).
   */
  private write(mutate: (map: TokenMap) => TokenMap | null): void {
    const base = this.readStorage() ?? this._tokens();
    const next = mutate(base);
    if (next === null) {
      // Nothing to write — still adopt what other tabs stored meanwhile.
      if (!sameTokenMap(base, this._tokens())) this._tokens.set(base);
      return;
    }
    const pruned = this.prune(next);
    this._tokens.set(pruned);
    this.saveToStorage(pruned);
  }

  /** Drops expired entries and keeps the `MAX_TOKENS` entries expiring last. */
  private prune(map: TokenMap, now: number = Date.now()): TokenMap {
    const entries = Object.entries(map).filter(([, e]) => e.expiresAt > now);
    entries.sort((a, b) => b[1].expiresAt - a[1].expiresAt);
    return Object.fromEntries(entries.slice(0, MAX_TOKENS));
  }

  /**
   * The stored map (pruned), `{}` when nothing is stored, or `null` when the
   * storage cannot be used (unavailable, throws, or holds unparsable data).
   */
  private readStorage(): TokenMap | null {
    if (!this.storageWritable || typeof localStorage === 'undefined') return null;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return {};
      const parsed: unknown = JSON.parse(stored);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      const result: TokenMap = {};
      for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
        const entry = this.parseEntry(value);
        if (entry) result[id] = entry;
      }
      return this.prune(result);
    } catch {
      return null;
    }
  }

  /**
   * Accepts the current `{ token, expiresAt }` shape and the legacy bare string;
   * drops entries whose token cannot be a header value (stored by an older
   * version, which sent the token as `?t=`, or edited by hand).
   */
  private parseEntry(value: unknown): StoredToken | null {
    if (typeof value === 'string') {
      return isHeaderSafeToken(value)
        ? { token: value, expiresAt: Date.now() + DEFAULT_TOKEN_TTL_MS }
        : null;
    }
    if (!value || typeof value !== 'object') return null;
    const { token, expiresAt } = value as Partial<StoredToken>;
    if (!isHeaderSafeToken(token)) return null;
    return {
      token,
      expiresAt: typeof expiresAt === 'number' ? expiresAt : Date.now() + DEFAULT_TOKEN_TTL_MS,
    };
  }

  private saveToStorage(tokens: TokenMap): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
      this.storageWritable = true;
    } catch {
      // private mode / quota — the in-memory map keeps working
      this.storageWritable = false;
    }
  }
}

function sameTokenMap(a: TokenMap, b: TokenMap): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((id) => {
    const other = b[id];
    return !!other && other.token === a[id].token && other.expiresAt === a[id].expiresAt;
  });
}
